import { LitElement, css, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { teknikTokens, teknikButtons } from '../styles/teknik-tokens.js';
import { tkPrimitives } from '../styles/tk-primitives.js';
import '../styles/tk-page.js';

@customElement('app-profile')
export class AppProfile extends LitElement {
  @property() username = '';
  @state() profile: any = null;
  @state() loading = true;
  @state() notFound = false;

  async connectedCallback() {
    super.connectedCallback();
    if (!this.username) {
      const raw = window.location.pathname.split('/u/')[1] || '';
      // Decode the path segment so usernames with spaces/unicode resolve — the
      // fetch below re-encodes it, so passing the raw %-encoded string would
      // double-encode (e.g. "%20" -> "%2520") and never match the account.
      try { this.username = decodeURIComponent(raw); } catch { this.username = raw; }
    }
    await this.loadProfile();
  }

  async loadProfile() {
    this.loading = true;
    try {
      const res = await fetch(`/api/profile/${encodeURIComponent(this.username)}`);
      if (res.status === 404) { this.notFound = true; }
      else { this.profile = await res.json(); }
    } catch { this.notFound = true; }
    this.loading = false;
  }

  getAvatarUrl() {
    if (!this.profile) return '';
    if (this.profile.avatar && this.profile.avatar.startsWith('data:')) return this.profile.avatar;
    if (this.profile.avatar) return this.profile.avatar;
    // Fall back to a generated avatar in the site accent rather than a stale
    // per-profile colour that no longer belongs to any palette here.
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(this.profile.username)}&background=4fc3ad&color=06110e&size=128`;
  }

  getMinecraftSkinUrl() {
    if (!this.profile?.minecraftUsername) return null;
    return `https://mc-heads.net/avatar/${this.profile.minecraftUsername}/64`;
  }

  static styles = [
    teknikTokens,
    teknikButtons,
    tkPrimitives,
    css`
      :host { display: block; }

      .hero {
        display: flex;
        align-items: center;
        gap: 20px;
        flex-wrap: wrap;
        padding: 1.75rem;
        margin-bottom: 12px;
      }

      .avatar {
        width: 84px; height: 84px; border-radius: 50%;
        overflow: hidden; flex-shrink: 0;
        border: 1px solid var(--tk-border-strong);
      }
      .avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }

      .who { flex: 1; min-width: 0; }
      .name {
        font-family: var(--font-display);
        font-size: 1.75rem; font-weight: 600; letter-spacing: -0.01em;
        color: var(--tk-text);
        overflow-wrap: anywhere;
      }
      .joined {
        font-family: var(--font-mono);
        font-size: .75rem; color: var(--tk-dim); margin-top: 4px;
      }
      .bio {
        font-size: .9rem; color: var(--tk-muted); line-height: 1.6; margin-top: 10px;
        max-width: 52ch;
      }

      .badges { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 10px; }

      .mc-skin { width: 26px; height: 26px; border-radius: 4px; image-rendering: pixelated; }

      .rows { display: flex; flex-direction: column; }
      .info-row {
        display: flex; align-items: center; gap: 10px;
        padding: .65rem 0;
        border-bottom: 1px solid var(--tk-border);
      }
      .info-row:last-child { border-bottom: none; }
      .info-label { font-size: .85rem; color: var(--tk-dim); flex: 1; }
      .info-value {
        font-size: .9rem; font-weight: 500; color: var(--tk-text);
        display: flex; align-items: center; gap: 8px;
      }

      .center { padding: 4rem 1rem; text-align: center; }
    `,
  ];

  render() {
    const heading = this.loading
      ? 'Profile'
      : this.notFound
      ? 'No such profile'
      : this.profile?.username || 'Profile';

    return html`
      <tk-page heading=${heading}>
        ${this.loading
          ? html`<div class="center"><span class="tk-spinner"></span></div>`
          : this.notFound
          ? html`
              <div class="tk-empty">
                <p>Nobody on Teknik goes by <strong>${this.username}</strong>.</p>
                <a class="tk-btn secondary" href="/" style="margin-top:1rem">Back to Teknik</a>
              </div>
            `
          : html`
              <div class="tk-panel hero">
                <div class="avatar">
                  <img src="${this.getAvatarUrl()}" alt="" />
                </div>
                <div class="who">
                  <div class="name">${this.profile.username}</div>
                  <div class="joined">
                    Joined ${this.profile.joinedAt
                      ? new Date(this.profile.joinedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
                      : 'Teknik'}
                  </div>
                  ${this.profile.bio ? html`<div class="bio">${this.profile.bio}</div>` : ''}
                  ${(this.profile.badges || []).length
                    ? html`<div class="badges">
                        ${(this.profile.badges || []).map((b: any) => html`<span class="tk-badge">${b.label}</span>`)}
                      </div>`
                    : ''}
                </div>
              </div>

              <div class="tk-panel">
                <div class="tk-panel-head">Account</div>
                <div class="tk-panel-body rows">
                  <div class="info-row">
                    <span class="info-label">Account type</span>
                    <span class="info-value">
                      ${this.profile.authType === 'teknik' ? 'Teknik account' : 'Discord account'}
                    </span>
                  </div>
                  ${this.profile.discordUsername
                    ? html`<div class="info-row">
                        <span class="info-label">Discord</span>
                        <span class="info-value">${this.profile.discordUsername}</span>
                      </div>`
                    : ''}
                  ${this.profile.minecraftUsername
                    ? html`<div class="info-row">
                        <span class="info-label">Minecraft</span>
                        <span class="info-value">
                          ${this.getMinecraftSkinUrl()
                            ? html`<img class="mc-skin" src="${this.getMinecraftSkinUrl()}" alt="" />`
                            : ''}
                          ${this.profile.minecraftUsername}
                        </span>
                      </div>`
                    : ''}
                </div>
              </div>
            `}
      </tk-page>
    `;
  }
}
